type ObserveReturn = (
  target: Function,
  context: ClassDecoratorContext<abstract new (...args: any) => any>,
) => void;

/**
 * Décorateur de classe pour Web Components permettant de déclarer les attributs HTML à observer.
 *
 * Ce décorateur simplifie la définition du getter statique `observedAttributes` requis par la spécification
 * des Custom Elements. Les attributs listés déclencheront l'exécution de `attributeChangedCallback`
 * lors de leur modification dans le DOM.
 *
 * @remarks
 * Le décorateur injecte une propriété statique interne `__CONFIG_ATTRIBS_TO_OBSERVE_` sur le constructeur de la classe.
 * La classe de base est responsable de lire cette configuration pour implémenter
 * le getter `static get observedAttributes()`.
 *
 * @param attribsToObserve1 - Le nom du premier attribut à observer (obligatoire pour garantir au moins un argument).
 * @param attribsToObserve - Une liste variable (Rest parameters) des autres noms d'attributs à observer.
 *
 * @throws {Error} Si le décorateur est appliqué sur un élément autre qu'une définition de classe (`context.kind !== 'class'`).
 *
 * @example
 * ```ts
 * ///
 * @Observe('disabled', 'value', 'theme')
 * class MyCustomInput extends BnumElement {
 * attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null) {
 * console.log(`L'attribut ${name} a changé de ${oldValue} à ${newValue}`);
 * }
 * }
 * ```
 */
export function Observe(
  attribsToObserve1: string,
  ...attribsToObserve: ReadonlyArray<string>
): ObserveReturn;
export function Observe(attribs: ReadonlyArray<string>): ObserveReturn;
export function Observe(
  attribsToObserve1: string | ReadonlyArray<string>,
  ...attribsToObserve: ReadonlyArray<string>
) {
  return function (target: Function, context: ClassDecoratorContext) {
    if (context.kind !== 'class') {
      throw new Error('@Observe ne peut être utilisé que sur une classe.');
    }

    context.addInitializer(function () {
      const attributesToObserve: string[] = Array.isArray(attribsToObserve1)
        ? attribsToObserve1
        : [attribsToObserve1, ...attribsToObserve];
      (this as any).__CONFIG_ATTRIBS_TO_OBSERVE_ = attributesToObserve;
    });
  };
}
